/**
 * Recipe catalog distribution: the numbers behind the CRM dashboard.
 *
 * Pure functions over `globals/admin_catalog_summary.recipes` and `globals/categories`, no DOM,
 * so they run under `node --test`. Every dimension reads the field the Cloud Function writes
 * into the summary (`buildCatalogRecipeSummary` in functions/ingredient_index_service.js) —
 * nothing is guessed from recipe titles.
 */

// The time histogram's bins, in order. Each is the upper bound in minutes, inclusive.
export const TIME_BUCKETS = [
  { id: "le_15", label: "≤ 15 min", max: 15 },
  { id: "16_20", label: "16–20 min", max: 20 },
  { id: "21_25", label: "21–25 min", max: 25 },
  { id: "26_30", label: "26–30 min", max: 30 },
  { id: "31_35", label: "31–35 min", max: 35 },
  { id: "36_45", label: "36–45 min", max: 45 },
  { id: "46_plus", label: "46+ min", max: Infinity }
];

// `taxonomy` names the globals/categories list a dimension's values come from:
// a `recipe_categories` category_id, or a top-level list ("dietary_preferences", "allergens").
// Protein and carb have no list in globals/categories, so only the values in use are known.
export const DIMENSIONS = [
  { key: "status", label: "Status", multi: false, read: (r) => r.status || "draft" },
  { key: "access", label: "Access", multi: false, read: (r) => (r.is_signature === true ? "signature" : "free") },
  { key: "protein", label: "Primary protein", multi: false, read: (r) => r.classification?.primary_protein || r.classification?.protein },
  { key: "cuisine", label: "Cuisine", multi: false, taxonomy: "cuisines_regions", read: (r) => r.classification?.cuisine },
  { key: "carb", label: "Carb base", multi: false, read: (r) => r.classification?.carb_category },
  { key: "method", label: "Cooking method", multi: false, taxonomy: "cooking_method_equipment", read: (r) => r.classification?.method },
  { key: "time", label: "Total time", multi: false, ordered: true, read: (r) => timeBucketOf(totalTimeOf(r))?.id },
  // Every catalog recipe is a dinner by design, so meal type is shown but never reported as a gap.
  { key: "meal_type", label: "Meal type", multi: false, taxonomy: "meal_type_course", noGaps: true, read: (r) => r.classification?.meal_type },
  { key: "diet", label: "Suits diet", multi: true, taxonomy: "dietary_preferences", read: (r) => r.dietary_preferences },
  { key: "allergen", label: "Contains allergen", multi: true, noGaps: true, taxonomy: "allergens", read: (r) => r.allergens },
  { key: "effort", label: "Time & effort tags", multi: true, taxonomy: "time_effort", read: (r) => r.classification?.time_effort },
  { key: "lifestyle", label: "Goals & lifestyle tags", multi: true, taxonomy: "goals_lifestyles", read: (r) => r.classification?.goals_lifestyles },
  { key: "occasion", label: "Occasion & season tags", multi: true, taxonomy: "occasions_seasons", read: (r) => r.classification?.occasions_seasons }
];

export const DIMENSION_BY_KEY = Object.fromEntries(DIMENSIONS.map((d) => [d.key, d]));

const FIXED_LABELS = {
  status: { published: "Published", draft: "Draft", archived: "Archived" },
  access: { free: "Free", signature: "Signature (Savvy)" },
  time: Object.fromEntries(TIME_BUCKETS.map((b) => [b.id, b.label]))
};

export function totalTimeOf(recipe) {
  const m = recipe?.planning_metrics || {};
  const total = Number(m.total_time_minutes || recipe?.total_time_minutes || 0);
  if (total > 0) return total;
  const parts = Number(m.prep_time_minutes || recipe?.prep_time_minutes || 0) + Number(m.cook_time_minutes || recipe?.cook_time_minutes || 0);
  return parts > 0 ? parts : null;
}

export function timeBucketOf(minutes) {
  if (!(minutes > 0)) return null;
  return TIME_BUCKETS.find((b) => minutes <= b.max) || null;
}

/** The recipe's values for one dimension, always an array of non-empty strings. */
export function valuesOf(recipe, dimKey) {
  const dim = DIMENSION_BY_KEY[dimKey];
  if (!dim) return [];
  const raw = dim.read(recipe);
  const list = Array.isArray(raw) ? raw : [raw];
  return [...new Set(list.filter((v) => typeof v === "string" && v.trim()).map((v) => v.trim()))];
}

/** Turns "middle_eastern" into "Middle Eastern" when no taxonomy name exists. */
export function humanize(id) {
  return String(id || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The known values for each dimension from globals/categories: `{ [dimKey]: [{ id, name }] }`.
 * Dimensions without a taxonomy are left out.
 */
export function buildTaxonomy(categoriesData = {}) {
  const lists = {};
  (categoriesData?.recipe_categories || []).forEach((cat) => {
    if (cat?.category_id) lists[cat.category_id] = cat.tags || [];
  });
  lists.dietary_preferences = categoriesData?.dietary_preferences || [];
  lists.allergens = categoriesData?.allergens || [];

  const taxonomy = {};
  DIMENSIONS.forEach((dim) => {
    if (!dim.taxonomy) return;
    const entries = (lists[dim.taxonomy] || [])
      .map((t) => ({ id: t.id || t.tag_id, name: t.name || humanize(t.id || t.tag_id) }))
      .filter((t) => t.id);
    if (entries.length > 0) taxonomy[dim.key] = entries;
  });
  return taxonomy;
}

export function labelFor(dimKey, value, taxonomy = {}) {
  if (FIXED_LABELS[dimKey]?.[value]) return FIXED_LABELS[dimKey][value];
  const hit = (taxonomy[dimKey] || []).find((t) => t.id === value);
  return hit ? hit.name : humanize(value);
}

/**
 * Recipes matching every filter. `filters` is `{ [dimKey]: value }`; a multi-valued dimension
 * matches when the recipe has that value among its values.
 */
export function filterRecipes(recipes, filters = {}) {
  const entries = Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (entries.length === 0) return recipes.slice();
  return recipes.filter((r) => entries.every(([dimKey, value]) => valuesOf(r, dimKey).includes(value)));
}

/**
 * Counts per value, most common first (time buckets stay in time order).
 * Returns `[{ value, count, share }]`, share being the fraction of `recipes`.
 * A multi-valued dimension's shares can add up to more than 1.
 */
export function countBy(recipes, dimKey) {
  const counts = new Map();
  recipes.forEach((r) => valuesOf(r, dimKey).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1)));
  const total = recipes.length || 1;
  const rows = [...counts.entries()].map(([value, count]) => ({ value, count, share: count / total }));
  if (dimKey === "time") {
    const order = TIME_BUCKETS.map((b) => b.id);
    return order.filter((id) => counts.has(id)).map((id) => rows.find((r) => r.value === id));
  }
  return rows.sort((a, b) => b.count - a.count || String(a.value).localeCompare(String(b.value)));
}

/** Every time bucket, empty ones included, for the histogram. */
export function timeHistogram(recipes) {
  const counts = Object.fromEntries(countBy(recipes, "time").map((r) => [r.value, r.count]));
  return TIME_BUCKETS.map((b) => ({ value: b.id, label: b.label, count: counts[b.id] || 0 }));
}

export function median(numbers) {
  const sorted = numbers.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Headline numbers for the KPI row. */
export function summarize(recipes, taxonomy = {}) {
  const times = recipes.map(totalTimeOf).filter((t) => t > 0);
  const cuisines = countBy(recipes, "cuisine");
  const proteins = countBy(recipes, "protein");
  return {
    total: recipes.length,
    published: recipes.filter((r) => r.status === "published").length,
    drafts: recipes.filter((r) => r.status !== "published").length,
    signature: recipes.filter((r) => r.is_signature === true).length,
    free: recipes.filter((r) => r.is_signature !== true).length,
    medianMinutes: median(times),
    under30: times.filter((t) => t <= 30).length,
    timed: times.length,
    cuisinesUsed: cuisines.length,
    cuisinesKnown: (taxonomy.cuisine || []).length,
    topCuisine: cuisines[0] || null,
    proteinsUsed: proteins.length,
    topProtein: proteins[0] || null
  };
}

/**
 * Taxonomy values with no or few recipes in `recipes`, per dimension:
 * `[{ dimKey, label, missing: [{ id, name }], thin: [{ id, name, count }] }]`.
 * Allergens and meal type are left out: a missing allergen is not a gap, and the catalog is dinners.
 */
export function coverageGaps(recipes, taxonomy = {}, { thinBelow = 3 } = {}) {
  const out = [];
  DIMENSIONS.forEach((dim) => {
    if (!taxonomy[dim.key] || dim.noGaps) return;
    const counts = Object.fromEntries(countBy(recipes, dim.key).map((r) => [r.value, r.count]));
    const missing = [];
    const thin = [];
    taxonomy[dim.key].forEach((t) => {
      const count = counts[t.id] || 0;
      if (count === 0) missing.push(t);
      else if (count < thinBelow) thin.push({ ...t, count });
    });
    if (missing.length || thin.length) out.push({ dimKey: dim.key, label: dim.label, missing, thin });
  });
  return out;
}

/**
 * A rows × columns count table. Rows and columns are the values in use, most common first.
 * `cells[rowValue][colValue]` is the number of recipes with both.
 */
export function crossTab(recipes, rowKey, colKey) {
  const rows = countBy(recipes, rowKey);
  const cols = countBy(recipes, colKey);
  const cells = {};
  rows.forEach((r) => { cells[r.value] = {}; });
  let max = 0;
  recipes.forEach((recipe) => {
    const rv = valuesOf(recipe, rowKey);
    const cv = valuesOf(recipe, colKey);
    rv.forEach((a) => cv.forEach((b) => {
      const next = (cells[a][b] || 0) + 1;
      cells[a][b] = next;
      if (next > max) max = next;
    }));
  });
  return { rows, cols, cells, max };
}

// Summary fields that ought to vary across a catalog. One value everywhere means it was never set.
// Base servings is left out: every recipe is written for 2 adults on purpose.
const VARIANCE_FIELDS = [
  { field: "difficulty_level", label: "Difficulty", read: (r) => r.difficulty_level },
  { field: "dish_load_score", label: "Dish load score", read: (r) => r.dish_load_score }
];

const LEGACY_CLASSIFICATION_KEYS = ["protein", "effort", "lifestyle", "occasion", "theme_tags"];

/**
 * Data problems worth fixing, each `{ id, severity: "warning"|"info", title, detail, recipeIds }`.
 */
export function dataQuality(recipes, taxonomy = {}) {
  const issues = [];
  const ids = (list) => list.map((r) => r.id || r.recipe_id);
  const push = (id, severity, title, detail, list) => {
    if (list.length > 0) issues.push({ id, severity, title, detail, recipeIds: ids(list) });
  };

  push("no_image", "warning", "No photo", "Shows a placeholder in the app.", recipes.filter((r) => !r.image_url && !r.thumbnail_url));
  push("no_time", "warning", "No total time", "Left out of every time filter and the planner's time limits.", recipes.filter((r) => !(totalTimeOf(r) > 0)));
  ["protein", "cuisine", "carb", "method"].forEach((key) => {
    const label = DIMENSION_BY_KEY[key].label.toLowerCase();
    push(`no_${key}`, "warning", `No ${label}`, `classification has no ${label}, so the recipe is missing from that breakdown.`, recipes.filter((r) => valuesOf(r, key).length === 0));
  });

  // Diet tags come from the ingredients; primary protein is set by hand. They should agree.
  push("veg_mismatch", "warning", "Protein and diet disagree",
    "Primary protein is vegetarian but the ingredients don't make it vegetarian, or the other way round.",
    recipes.filter((r) => {
      const protein = valuesOf(r, "protein")[0];
      if (!protein) return false;
      const isVegDiet = valuesOf(r, "diet").includes("vegetarian");
      return (protein === "vegetarian") !== isVegDiet;
    }));

  // The time tags are set by hand and the apps filter on them, so they must match the minutes.
  push("time_tag_mismatch", "warning", "Time tag contradicts the minutes",
    "Tagged Under 15 or Under 30 Mins but the total time is longer.",
    recipes.filter((r) => {
      const minutes = totalTimeOf(r);
      const tags = valuesOf(r, "effort");
      return minutes > 0 && ((tags.includes("under_15_mins") && minutes > 15) || (tags.includes("under_30_mins") && minutes > 30));
    }));
  push("time_tag_missing", "info", "Quick but not tagged Under 30 Mins",
    "Takes 30 minutes or less, yet won't show under the Under 30 Mins filter.",
    recipes.filter((r) => {
      const minutes = totalTimeOf(r);
      return minutes > 0 && minutes <= 30 && !valuesOf(r, "effort").includes("under_30_mins");
    }));

  Object.keys(taxonomy).forEach((dimKey) => {
    const known = new Set(taxonomy[dimKey].map((t) => t.id));
    const unknownValues = new Set();
    const list = recipes.filter((r) => {
      const bad = valuesOf(r, dimKey).filter((v) => !known.has(v));
      bad.forEach((v) => unknownValues.add(v));
      return bad.length > 0;
    });
    push(`unknown_${dimKey}`, "warning", `${DIMENSION_BY_KEY[dimKey].label}: value not in Categories`,
      `${[...unknownValues].sort().join(", ")} — not a tag in globals/categories, so the apps can't filter by it.`, list);
  });

  push("legacy_keys", "info", "Legacy classification keys",
    `Carries old keys (${LEGACY_CLASSIFICATION_KEYS.join(", ")}) next to the current ones.`,
    recipes.filter((r) => LEGACY_CLASSIFICATION_KEYS.some((k) => r.classification && k in r.classification)));

  if (recipes.length >= 5) {
    VARIANCE_FIELDS.forEach((f) => {
      const values = new Set(recipes.map((r) => JSON.stringify(f.read(r) ?? null)));
      if (values.size === 1) {
        const only = JSON.parse([...values][0]);
        issues.push({
          id: `flat_${f.field}`,
          severity: "info",
          title: `${f.label} is the same everywhere`,
          detail: `Every recipe has ${f.field} = ${only === null ? "nothing" : JSON.stringify(only)}, so it tells recipes apart by nothing.`,
          recipeIds: []
        });
      }
    });
  }

  return issues;
}

// recipe-builder's coverage-target facets (data/coverage_targets.json, published to globals/coverage_targets) and the
// dashboard dimension each one measures. allergen_free counts the recipes *without* the allergen.
export const TARGET_FACETS = {
  dietary_preferences: { dimKey: "diet" },
  allergens: { dimKey: "allergen" },
  allergen_free: { dimKey: "allergen", without: true },
  primary_protein: { dimKey: "protein" },
  carb_category: { dimKey: "carb" },
  cuisines_regions: { dimKey: "cuisine" },
  cooking_method_equipment: { dimKey: "method" },
  meal_type_course: { dimKey: "meal_type" },
  time_effort: { dimKey: "effort" },
  goals_lifestyles: { dimKey: "lifestyle" },
  occasions_seasons: { dimKey: "occasion" }
};

/**
 * Every goal scored the way recipe-builder's scripts/report_category_distribution.py scores it (compute_gaps):
 * `[{ facet, dimKey, without, tag, count, share, min, max, status: "met"|"under"|"over", needed }]`.
 * `needed` is how many recipes to add — with the tag when under, without it when over — to meet the goal.
 * Missed goals come first, under before over, most recipes needed first. Unknown facets are skipped.
 */
export function evaluateGoals(recipes, targets = {}) {
  const total = recipes.length;
  const goals = [];
  Object.entries(targets || {}).forEach(([facet, tags]) => {
    const map = TARGET_FACETS[facet];
    if (!map || !tags || typeof tags !== "object") return;
    Object.entries(tags).forEach(([tag, target]) => {
      const min = typeof target?.min_share === "number" ? target.min_share : null;
      const max = typeof target?.max_share === "number" ? target.max_share : null;
      if (min === null && max === null) return;
      const has = recipes.filter((r) => valuesOf(r, map.dimKey).includes(tag)).length;
      const count = map.without ? total - has : has;
      const goal = { facet, dimKey: map.dimKey, without: Boolean(map.without), tag, count, share: total ? count / total : 0, min, max, status: "met", needed: 0 };
      if (min !== null && count < Math.ceil(min * total)) {
        // Adding n recipes that carry the tag: (count + n) / (total + n) >= min.
        goal.status = "under";
        goal.needed = min < 1 ? Math.ceil((min * total - count) / (1 - min)) : null;
      } else if (max !== null && count > max * total) {
        // Adding n recipes without the tag: count / (total + n) <= max.
        goal.status = "over";
        goal.needed = Math.ceil(count / max - total);
      }
      goals.push(goal);
    });
  });
  const rank = { under: 0, over: 1, met: 2 };
  return goals.sort((a, b) => rank[a.status] - rank[b.status] || (b.needed || 0) - (a.needed || 0) || a.tag.localeCompare(b.tag));
}

/**
 * A goal as a share range of the recipes that *have* the value, for drawing on that dimension's bars:
 * `{ [dimKey]: { [value]: { min, max, goal } } }`. An allergen-free goal of at least 35% becomes "contains at most 65%".
 */
export function goalMarkers(goals) {
  const out = {};
  goals.forEach((goal) => {
    const range = goal.without
      ? { min: goal.max !== null ? 1 - goal.max : null, max: goal.min !== null ? 1 - goal.min : null }
      : { min: goal.min, max: goal.max };
    out[goal.dimKey] = out[goal.dimKey] || {};
    out[goal.dimKey][goal.tag] = { ...range, goal };
  });
  return out;
}
